import type { SessionEntityType, UserSession } from "../api/models/session";
import type { ClassSubObject } from "../api/models/students";
import { encodeURLString, parseURL, type URLDataType } from "../Transitions/ParseURL";
import { currentEnv, Env } from "../utils/env";
import fakeNames from "./fakeNames.json";
import type { IslandDetails } from "./IslandManager";
import { StudentManager } from "./StudentManager";
import { TeacherManager } from "./TeacherManager";
import { UserManager } from "./UserManager";

type PartialIslandDetails = Omit<IslandDetails, "islandName">;

function getIslandDetails(
  classroom: ClassSubObject,
  teacher?: { title?: string; lastName?: string; avatarURL?: string }
): PartialIslandDetails {
  //Get the teacher title + last name or default to "Welcome to"
  let ownerName = "Welcome to";
  if (teacher && teacher.lastName != null) {
    ownerName = `${teacher.title?.trim() || ""} ${teacher.lastName.trim()}`.trim();
    ownerName = makePossessive(ownerName);
  }

  return {
    id: `class-${classroom._id}`,
    ownerName,
    ownerAvatarURL: teacher?.avatarURL ?? "",
  };
}

function makePossessive(name: string) {
  if (name.substring(name.length - 1) === "s") {
    name += "'";
  } else {
    name += "'s";
  }
  return name;
}

export function getClassIslands(entityType: SessionEntityType) {
  const islands: PartialIslandDetails[] = [];
  if (entityType === "teacher") {
    for (const classroom of TeacherManager.instance.sortedClasses) {
      if (!classroom.archived) {
        const classOwner = classroom.collaborators.find((teacher) => teacher.sharingStatus === "owner");
        const islandOwner = classOwner && {
          title: classOwner.title,
          lastName: classOwner.lastName,
          avatarURL: classOwner._links?.photo?.href,
        };
        islands.push(getIslandDetails(classroom, islandOwner));
      }
    }
  } else {
    for (const renderedClass of StudentManager.instance.sortedClasses) {
      if (!renderedClass.archived || renderedClass.recentlyArchived) {
        islands.push(getIslandDetails(renderedClass, renderedClass.teacher));
      }
    }
  }
  return islands;
}

export function getPersonalOrHomeIsland(groupId: string): PartialIslandDetails {
  const user = getUserNameInfo().shortName;
  const ownerAvatarURL =
    (UserManager.instance.user?.type === "student"
      ? UserManager.instance.user?.student?.avatarURL
      : UserManager.instance.user?.teacher?.avatarURL) || "";
  return {
    id: groupId,
    ownerName: makePossessive(user),
    ownerAvatarURL,
  };
}

export function findIsland(groupId: string, entityType: SessionEntityType): PartialIslandDetails | undefined {
  if (groupId.startsWith("personal-") || groupId.startsWith("community-")) {
    return getPersonalOrHomeIsland(groupId);
  }

  const islands = getClassIslands(entityType);
  for (const island of islands) {
    if (island.id === groupId) {
      return island;
    }
  }
}

export type UserNameInfo = {
  shortName: string;
  fullName: string;
};

export function getStudentShortName(firstName: string, lastName: string) {
  let shortName: string;
  //Create an abbreviated version of first name with last initial in the ideal case
  if (firstName.length > 0) {
    shortName = firstName.trim();

    if (lastName.length > 0) {
      shortName += ` ${lastName[0]}`;
    }
  } else {
    shortName = lastName.trim();
  }

  return shortName;
}

export function getUserNameInfo(session: UserSession | undefined = UserManager.instance.user): UserNameInfo {
  if (session?.type === "teacher") {
    const title = session.teacher.title || "";
    const { firstName, lastName } = getFirstLastName(session.teacher);

    //Assign to user
    const firstLastName = `${firstName} ${lastName}`.trim();
    const fullName = `${title} ${firstLastName}`.trim();
    const shortName = `${title} ${lastName || firstName}`.trim();

    return { fullName, shortName };
  } else if (session?.type === "student") {
    const { firstName, lastName } = getFirstLastName(session.student);
    return { fullName: `${firstName} ${lastName}`.trim(), shortName: getStudentShortName(firstName, lastName) };
  }

  //TODO: Log error
  return { fullName: "Undefined User", shortName: "UUser" };
}

function getFirstLastName(entity: { _id: string; firstName: string; lastName: string }) {
  let firstName = (entity.firstName ?? "").trim();
  let lastName = (entity.lastName ?? "").trim();

  //If the user doesn't have a first or last name, use an adjective/noun to create a fake one for them
  if (firstName === "" && lastName === "") {
    //Index off of their ID to stay consistent in future sessions
    //Grab the first 8 digits of ID to ensure the number isn't crazy large
    const firstPartID = entity._id.substring(0, Math.min(8, entity._id.length - 1));

    //Convert from hex to decimal number
    const hash = parseInt(firstPartID, 16);

    //Index into the fake names
    firstName = fakeNames.firstNames[hash % fakeNames.firstNames.length];
    lastName = fakeNames.lastNames[(hash * 7) % fakeNames.lastNames.length];
  }

  firstName = capitalizeFirstLetter(firstName);
  lastName = capitalizeFirstLetter(lastName);

  return { firstName, lastName };
}

function capitalizeFirstLetter(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

let lastOptionsLog = "";
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getLauncherStartupOptions(): {
  entityType: SessionEntityType;
  islandId?: string;
  startupParams: string;
  URLdata?: URLDataType;
} {
  const URLdata = parseURL();
  let launchDojoIslandsAs: SessionEntityType = URLdata?.entityType === "teacher" ? "teacher" : "student";
  let islandId = URLdata?.islandId;
  if (
    islandId &&
    !islandId.startsWith("class-") &&
    !islandId.startsWith("personal-") &&
    !islandId.startsWith("home-") &&
    !islandId.startsWith("community-")
  ) {
    islandId = `class-${islandId}`;
  }

  if (currentEnv() !== Env.Prod) {
    const optionsLog = `getLauncherStartupOptions(): entityType=[${launchDojoIslandsAs}] islandId=[${islandId}]`;
    if (optionsLog !== lastOptionsLog) {
      lastOptionsLog = optionsLog;
      console.log(optionsLog);
    }
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  const startupParams = urlSearchParams.get("startupParams") || "";

  //Allow a simpler param (non encoded) for switching island ids locally
  if (currentEnv() === Env.Local && !islandId && urlSearchParams.has("islandType")) {
    switch (urlSearchParams.get("islandType") ?? "class") {
      case "class":
        islandId = "class-abcdefg";
        break;
      case "personal":
        islandId = "personal-abcdefg";
        break;
      case "community":
        islandId = "community-abcdefg";
        break;
    }
  } else if (currentEnv() === Env.Dev && !islandId && urlSearchParams.has("islandType")) {
    switch (urlSearchParams.get("islandType") ?? "class") {
      case "class":
      case "personal":
        islandId = "default";
        break;
      case "community":
        islandId = "defaultCommunity";
        break;
    }
  }

  // Allow full override of groupId LOCALLY for testing
  if (currentEnv() === Env.Local && !islandId && urlSearchParams.has("islandId")) {
    islandId = urlSearchParams.get("islandId") ?? undefined;
  }

  //Allow a simpler param (non encoded) for switching entity types locally
  if (currentEnv() === Env.Local && urlSearchParams.has("entityType")) {
    launchDojoIslandsAs = urlSearchParams.get("entityType") === "teacher" ? "teacher" : "student";
  }

  return {
    entityType: launchDojoIslandsAs,
    islandId,
    startupParams,
    URLdata,
  };
}

export function getUpdatedLauncherStartupOptions(newIslandId: string, startupParams?: Record<string, string>) {
  let URLdata = parseURL();
  if (!URLdata) {
    URLdata = { islandId: newIslandId };
  } else {
    const { entityType, spawnPoint } = URLdata;
    URLdata = { islandId: newIslandId, entityType, spawnPoint };
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.set("data", encodeURLString(JSON.stringify(URLdata)));

  if (startupParams) {
    urlSearchParams.set("startupParams", encodeURLString(JSON.stringify(startupParams)));
  }

  return urlSearchParams.toString();
}
